/**
 * COASegments.jsx — AOS-FIN-COA-03: Dimension / Segment Configuration
 *
 * Two-panel interface:
 *   Left  — dimension list (DEPARTMENT, COST_CENTER, FUND, PROJECT, GRANT, CUSTOM)
 *   Right — dimension detail: values tree with two-level hierarchy
 *
 * RBAC:
 *   Read:    finance.coa.read  (displayed, but write controls hidden)
 *   Manage:  finance.coa.segment.manage  (CFO, institution_admin, super_admin)
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useInstitution } from "../context/InstitutionContext";
import { toast } from "sonner";
import {
  Layers, Plus, RefreshCw, ChevronRight, ChevronDown,
  Tag, Settings, ToggleLeft, ToggleRight, AlertTriangle,
  CheckCircle2, XCircle, Edit3, FolderTree, Building2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  DEPARTMENT:  { label: "Department",  color: "bg-blue-100 text-blue-800 border-blue-200" },
  COST_CENTER: { label: "Cost Center", color: "bg-green-100 text-green-800 border-green-200" },
  FUND:        { label: "Fund",        color: "bg-purple-100 text-purple-800 border-purple-200" },
  PROJECT:     { label: "Project",     color: "bg-amber-100 text-amber-800 border-amber-200" },
  GRANT:       { label: "Grant",       color: "bg-rose-100 text-rose-800 border-rose-200" },
  PROGRAM:     { label: "Program",     color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  INSTITUTION: { label: "Institution", color: "bg-slate-100 text-slate-700 border-slate-200" },
  CAMPUS:      { label: "Campus",      color: "bg-teal-100 text-teal-800 border-teal-200" },
  CUSTOM:      { label: "Custom",      color: "bg-orange-100 text-orange-800 border-orange-200" },
};

const MANAGE_ROLES = new Set(["super_admin", "institution_admin", "cfo"]);

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.CUSTOM;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function StatusDot({ status }) {
  return status === "ACTIVE"
    ? <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" title="Active" />
    : <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" title="Inactive" />;
}

// ── Dimension Create/Edit Dialog ───────────────────────────────────────────

function DimensionDialog({ open, onClose, onSave, initial }) {
  const isEdit = !!initial;
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState(initial?.dimension_type ?? "DEPARTMENT");
  const [isMandatory, setIsMandatory] = useState(initial?.is_mandatory ?? false);
  const [allowsMultiple, setAllowsMultiple] = useState(initial?.allows_multiple ?? false);
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(initial?.code ?? "");
      setName(initial?.name ?? "");
      setType(initial?.dimension_type ?? "DEPARTMENT");
      setIsMandatory(initial?.is_mandatory ?? false);
      setAllowsMultiple(initial?.allows_multiple ?? false);
      setSortOrder(initial?.sort_order ?? 0);
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) {
      toast.error("Code and name are required.");
      return;
    }
    setSaving(true);
    try {
      await onSave({ code, name, dimension_type: type, is_mandatory: isMandatory, allows_multiple: allowsMultiple, sort_order: Number(sortOrder) });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dimension-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Dimension" : "Create Dimension"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code <span className="text-red-500">*</span></Label>
              <Input
                data-testid="dim-code-input"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                disabled={isEdit}
                placeholder="e.g. DEPT"
                maxLength={40}
              />
              {isEdit && <p className="text-xs text-slate-400 mt-1">Code is immutable (INV-COA-03-05)</p>}
            </div>
            <div>
              <Label>Sort Order</Label>
              <Input
                data-testid="dim-sort-input"
                type="number"
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Name <span className="text-red-500">*</span></Label>
            <Input
              data-testid="dim-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Department"
              maxLength={160}
            />
          </div>
          {!isEdit && (
            <div>
              <Label>Dimension Type <span className="text-red-500">*</span></Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="dim-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Type is immutable after creation (INV-COA-03-05)</p>
            </div>
          )}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="dim-mandatory-check"
                checked={isMandatory}
                onChange={e => setIsMandatory(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm">Mandatory on journal lines</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="dim-multiple-check"
                checked={allowsMultiple}
                onChange={e => setAllowsMultiple(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm">Allows multiple values</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="dim-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Dimension"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Value Create/Edit Dialog ──────────────────────────────────────────────

function ValueDialog({ open, onClose, onSave, parentOptions, initial }) {
  const isEdit = !!initial;
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [parentId, setParentId] = useState(initial?.parent_value_id ?? "__none__");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCode(initial?.code ?? "");
      setName(initial?.name ?? "");
      setParentId(initial?.parent_value_id ?? "__none__");
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) {
      toast.error("Code and name are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { code, name };
      if (isEdit) {
        if (parentId === "__none__") payload.clear_parent = true;
        else payload.parent_value_id = parentId;
      } else {
        if (parentId !== "__none__") payload.parent_value_id = parentId;
      }
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const topLevelOptions = (parentOptions || []).filter(v => !v.parent_value_id && v.id !== initial?.id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="value-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Value" : "Add Value"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code <span className="text-red-500">*</span></Label>
              <Input
                data-testid="val-code-input"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. CSE"
                maxLength={40}
              />
            </div>
          </div>
          <div>
            <Label>Name <span className="text-red-500">*</span></Label>
            <Input
              data-testid="val-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Computer Science & Engineering"
              maxLength={200}
            />
          </div>
          <div>
            <Label>Parent Value <span className="text-slate-400 text-xs">(optional — max 2 levels)</span></Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger data-testid="val-parent-select">
                <SelectValue placeholder="No parent (top-level)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No parent (top-level) —</SelectItem>
                {topLevelOptions.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.code} — {v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="val-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Value"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────

function ConfirmDialog({ open, title, message, onConfirm, onClose, danger = true }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try { await onConfirm(); onClose(); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {danger && <AlertTriangle className="w-5 h-5 text-amber-500" />}
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{message}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            data-testid="confirm-action-btn"
            variant={danger ? "destructive" : "default"}
            onClick={handle}
            disabled={loading}
          >
            {loading ? "Working…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function COASegments() {
  const { user, getToken } = useAuth();
  const { selectedInstitution } = useInstitution();

  const canManage = MANAGE_ROLES.has(user?.role);
  const instId = user?.role === "super_admin"
    ? (selectedInstitution?.id || null)
    : (user?.institution_id || null);

  const [dims, setDims] = useState([]);
  const [loadingDims, setLoadingDims] = useState(false);
  const [selectedDim, setSelectedDim] = useState(null);
  const [values, setValues] = useState([]);
  const [loadingVals, setLoadingVals] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ACTIVE");

  // Dialogs
  const [showDimCreate, setShowDimCreate] = useState(false);
  const [editDim, setEditDim] = useState(null);
  const [showValCreate, setShowValCreate] = useState(false);
  const [editVal, setEditVal] = useState(null);
  const [confirm, setConfirm] = useState(null); // {title, message, action}

  const authHeader = useCallback(async () => {
    const token = await getToken?.() || localStorage.getItem("academiaos_token");
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  }, [getToken]);

  const loadDims = useCallback(async () => {
    if (!instId) return;
    setLoadingDims(true);
    try {
      const headers = await authHeader();
      const r = await fetch(
        `${API}/api/finance/coa/segments?institution_id=${instId}&status=${statusFilter === "ALL" ? "ALL" : statusFilter}`,
        { headers }
      );
      if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
      const data = await r.json();
      setDims(data);
      // Auto-select first or keep current
      if (data.length > 0 && !selectedDim) setSelectedDim(data[0]);
      else if (selectedDim) {
        const updated = data.find(d => d.id === selectedDim.id);
        setSelectedDim(updated || data[0] || null);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingDims(false);
    }
  }, [instId, statusFilter, authHeader, selectedDim]);

  const loadValues = useCallback(async (dim) => {
    if (!dim || !instId) return;
    setLoadingVals(true);
    try {
      const headers = await authHeader();
      const r = await fetch(
        `${API}/api/finance/coa/segments/${dim.id}/values?institution_id=${instId}&status=ALL`,
        { headers }
      );
      if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
      setValues(await r.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingVals(false);
    }
  }, [instId, authHeader]);

  useEffect(() => { loadDims(); }, [instId, statusFilter]);
  useEffect(() => { if (selectedDim) loadValues(selectedDim); else setValues([]); }, [selectedDim]);

  // ── Dimension actions ───────────────────────────────────────────────────

  const createDimension = async (payload) => {
    const headers = await authHeader();
    const r = await fetch(`${API}/api/finance/coa/segments?institution_id=${instId}`, {
      method: "POST", headers, body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success("Dimension created.");
    await loadDims();
  };

  const updateDimension = async (id, payload) => {
    const headers = await authHeader();
    const r = await fetch(`${API}/api/finance/coa/segments/${id}?institution_id=${instId}`, {
      method: "PATCH", headers, body: JSON.stringify(payload),
    });
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success("Dimension updated.");
    await loadDims();
  };

  const toggleDimStatus = async (dim) => {
    const action = dim.status === "ACTIVE" ? "deactivate" : "reactivate";
    const headers = await authHeader();
    const r = await fetch(
      `${API}/api/finance/coa/segments/${dim.id}/${action}?institution_id=${instId}`,
      { method: "POST", headers }
    );
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success(`Dimension ${action === "deactivate" ? "deactivated" : "reactivated"}.`);
    await loadDims();
  };

  // ── Value actions ───────────────────────────────────────────────────────

  const createValue = async (payload) => {
    const headers = await authHeader();
    const r = await fetch(
      `${API}/api/finance/coa/segments/${selectedDim.id}/values?institution_id=${instId}`,
      { method: "POST", headers, body: JSON.stringify(payload) }
    );
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success("Value created.");
    await loadValues(selectedDim);
    await loadDims();
  };

  const updateValue = async (id, payload) => {
    const headers = await authHeader();
    const r = await fetch(
      `${API}/api/finance/coa/segments/${selectedDim.id}/values/${id}?institution_id=${instId}`,
      { method: "PATCH", headers, body: JSON.stringify(payload) }
    );
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success("Value updated.");
    await loadValues(selectedDim);
  };

  const toggleValueStatus = async (val) => {
    const action = val.status === "ACTIVE" ? "deactivate" : "reactivate";
    const headers = await authHeader();
    const r = await fetch(
      `${API}/api/finance/coa/segments/${selectedDim.id}/values/${val.id}/${action}?institution_id=${instId}`,
      { method: "POST", headers }
    );
    if (!r.ok) throw new Error((await r.json()).detail || r.statusText);
    toast.success(`Value ${action === "deactivate" ? "deactivated" : "reactivated"}.`);
    await loadValues(selectedDim);
    await loadDims();
  };

  // ── Hierarchy tree helpers ──────────────────────────────────────────────

  const topLevel = values.filter(v => !v.parent_value_id);
  const childrenOf = (parentId) => values.filter(v => v.parent_value_id === parentId);

  // ── No institution selected (super_admin) ───────────────────────────────
  if (!instId) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="coa-segments-page">
        <div className="text-center text-slate-500">
          <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Select an institution</p>
          <p className="text-sm mt-1">Use the institution switcher to choose an institution.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="coa-segments-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Layers className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Segment Configuration</h1>
            <p className="text-xs text-slate-500">COA-03 · Financial dimension & segment management</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8 text-xs" data-testid="status-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
              <SelectItem value="ALL">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={loadDims} disabled={loadingDims} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loadingDims ? "animate-spin" : ""}`} />
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setShowDimCreate(true)} data-testid="create-dimension-btn">
              <Plus className="w-4 h-4 mr-1" /> New Dimension
            </Button>
          )}
        </div>
      </div>

      {/* Body — 2-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — dimension list */}
        <div className="w-72 border-r border-slate-200 bg-white overflow-y-auto flex-shrink-0" data-testid="dimensions-panel">
          <div className="p-3 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Dimensions ({dims.length})
            </p>
          </div>
          {loadingDims ? (
            <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>
          ) : dims.length === 0 ? (
            <div className="p-6 text-center">
              <FolderTree className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No dimensions found.</p>
              {canManage && (
                <Button size="sm" variant="link" onClick={() => setShowDimCreate(true)} className="mt-1">
                  Create one
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {dims.map(dim => (
                <button
                  key={dim.id}
                  data-testid={`dimension-item-${dim.code}`}
                  onClick={() => setSelectedDim(dim)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors group ${
                    selectedDim?.id === dim.id ? "bg-indigo-50 border-r-2 border-indigo-500" : ""
                  } ${dim.status === "INACTIVE" ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusDot status={dim.status} />
                      <span className="font-mono text-xs text-slate-500">{dim.code}</span>
                    </div>
                    <TypeBadge type={dim.dimension_type} />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm font-medium text-slate-800">{dim.name}</span>
                    <span className="text-xs text-slate-400">{dim.value_count} val{dim.value_count !== 1 ? "s" : ""}</span>
                  </div>
                  {(dim.is_mandatory || dim.allows_multiple) && (
                    <div className="flex gap-1 mt-1">
                      {dim.is_mandatory && <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Mandatory</span>}
                      {dim.allows_multiple && <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Multi</span>}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right panel — dimension detail */}
        <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="dimension-detail-panel">
          {!selectedDim ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-slate-400">
                <Layers className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>Select a dimension to view its values.</p>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {/* Dim header card */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <StatusDot status={selectedDim.status} />
                      <span className="font-mono text-sm text-slate-500">{selectedDim.code}</span>
                      <TypeBadge type={selectedDim.dimension_type} />
                      {selectedDim.is_mandatory && <Badge variant="outline" className="text-xs">Mandatory</Badge>}
                      {selectedDim.allows_multiple && <Badge variant="outline" className="text-xs">Multi-value</Badge>}
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900" data-testid="selected-dim-name">
                      {selectedDim.name}
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">
                      Sort order: {selectedDim.sort_order} · Version: {selectedDim.version} · {selectedDim.value_count} value{selectedDim.value_count !== 1 ? "s" : ""}
                    </p>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setEditDim(selectedDim)}
                        data-testid="edit-dimension-btn"
                      >
                        <Edit3 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedDim.status === "ACTIVE" ? "outline" : "default"}
                        onClick={() => setConfirm({
                          title: selectedDim.status === "ACTIVE" ? "Deactivate Dimension" : "Reactivate Dimension",
                          message: selectedDim.status === "ACTIVE"
                            ? "All active values must be deactivated first. This will mark the dimension as inactive."
                            : "This will reactivate the dimension and allow new values to be added.",
                          action: () => toggleDimStatus(selectedDim),
                          danger: selectedDim.status === "ACTIVE",
                        })}
                        data-testid="toggle-dim-status-btn"
                      >
                        {selectedDim.status === "ACTIVE"
                          ? <><ToggleLeft className="w-4 h-4 mr-1" /> Deactivate</>
                          : <><ToggleRight className="w-4 h-4 mr-1" /> Reactivate</>
                        }
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Values section */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <h3 className="font-medium text-slate-800 flex items-center gap-2">
                    <Tag className="w-4 h-4 text-slate-400" />
                    Values
                    <span className="text-xs text-slate-400 font-normal">
                      ({values.filter(v => v.status === "ACTIVE").length} active
                      {values.filter(v => v.status !== "ACTIVE").length > 0
                        ? ` · ${values.filter(v => v.status !== "ACTIVE").length} inactive`
                        : ""})
                    </span>
                  </h3>
                  {canManage && selectedDim.status === "ACTIVE" && (
                    <Button size="sm" variant="outline" onClick={() => setShowValCreate(true)} data-testid="add-value-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Value
                    </Button>
                  )}
                </div>

                {loadingVals ? (
                  <div className="p-8 text-center text-slate-400 text-sm">Loading values…</div>
                ) : values.length === 0 ? (
                  <div className="p-8 text-center">
                    <Tag className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">No values yet.</p>
                    {canManage && selectedDim.status === "ACTIVE" && (
                      <Button size="sm" variant="link" onClick={() => setShowValCreate(true)}>Add first value</Button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100" data-testid="values-list">
                    {topLevel.map(parent => (
                      <ValueRow
                        key={parent.id}
                        val={parent}
                        children={childrenOf(parent.id)}
                        canManage={canManage && selectedDim.status === "ACTIVE"}
                        onEdit={setEditVal}
                        onToggle={(val) => setConfirm({
                          title: val.status === "ACTIVE" ? "Deactivate Value" : "Reactivate Value",
                          message: val.status === "ACTIVE"
                            ? "This value will be marked inactive. Journal lines referencing it will not be affected."
                            : "This value will be reactivated.",
                          action: () => toggleValueStatus(val),
                          danger: val.status === "ACTIVE",
                        })}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Invariant notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-700 space-y-0.5">
                  <p><strong>INV-COA-03-08:</strong> Values referenced by posted journal lines cannot be deactivated.</p>
                  <p><strong>INV-COA-03-06:</strong> A dimension with active values cannot be deactivated.</p>
                  <p><strong>INV-COA-03-04:</strong> Hierarchy is limited to two levels (parent → child only).</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <DimensionDialog
        open={showDimCreate}
        onClose={() => setShowDimCreate(false)}
        onSave={createDimension}
      />
      <DimensionDialog
        open={!!editDim}
        onClose={() => setEditDim(null)}
        onSave={(payload) => updateDimension(editDim.id, payload)}
        initial={editDim}
      />
      <ValueDialog
        open={showValCreate}
        onClose={() => setShowValCreate(false)}
        onSave={createValue}
        parentOptions={values}
      />
      <ValueDialog
        open={!!editVal}
        onClose={() => setEditVal(null)}
        onSave={(payload) => updateValue(editVal.id, payload)}
        initial={editVal}
        parentOptions={values}
      />
      {confirm && (
        <ConfirmDialog
          open
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.action}
          onClose={() => setConfirm(null)}
          danger={confirm.danger}
        />
      )}
    </div>
  );
}

// ── ValueRow ──────────────────────────────────────────────────────────────

function ValueRow({ val, children = [], canManage, onEdit, onToggle }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = children.length > 0;

  return (
    <div data-testid={`value-row-${val.code}`}>
      <div className={`flex items-center gap-3 px-5 py-3 hover:bg-slate-50 group ${val.status === "INACTIVE" ? "opacity-50" : ""}`}>
        <button
          onClick={() => hasChildren && setExpanded(e => !e)}
          className={`w-4 flex-shrink-0 ${hasChildren ? "cursor-pointer text-slate-400 hover:text-slate-600" : "text-transparent pointer-events-none"}`}
        >
          {hasChildren
            ? (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)
            : <span className="w-4" />}
        </button>

        <StatusDot status={val.status} />
        <span className="font-mono text-xs text-slate-500 w-20 truncate">{val.code}</span>
        <span className="text-sm text-slate-800 flex-1">{val.name}</span>

        {canManage && (
          <div className="hidden group-hover:flex items-center gap-1">
            <Button
              size="sm" variant="ghost"
              className="h-7 px-2"
              onClick={() => onEdit(val)}
              data-testid={`edit-value-btn-${val.code}`}
            >
              <Edit3 className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm" variant="ghost"
              className="h-7 px-2"
              onClick={() => onToggle(val)}
              data-testid={`toggle-value-btn-${val.code}`}
            >
              {val.status === "ACTIVE"
                ? <XCircle className="w-3.5 h-3.5 text-rose-400" />
                : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
            </Button>
          </div>
        )}
      </div>

      {/* Children (level 2) */}
      {hasChildren && expanded && (
        <div className="border-l-2 border-indigo-100 ml-10">
          {children.map(child => (
            <div
              key={child.id}
              data-testid={`value-row-${child.code}`}
              className={`flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 group ${child.status === "INACTIVE" ? "opacity-50" : ""}`}
            >
              <span className="w-4 text-slate-300 flex-shrink-0">└</span>
              <StatusDot status={child.status} />
              <span className="font-mono text-xs text-slate-500 w-20 truncate">{child.code}</span>
              <span className="text-sm text-slate-700 flex-1">{child.name}</span>
              {canManage && (
                <div className="hidden group-hover:flex items-center gap-1">
                  <Button
                    size="sm" variant="ghost" className="h-7 px-2"
                    onClick={() => onEdit(child)}
                    data-testid={`edit-value-btn-${child.code}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="ghost" className="h-7 px-2"
                    onClick={() => onToggle(child)}
                    data-testid={`toggle-value-btn-${child.code}`}
                  >
                    {child.status === "ACTIVE"
                      ? <XCircle className="w-3.5 h-3.5 text-rose-400" />
                      : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
